import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { importOssOrdersExcel } from "../api/ossApi";
import { OssUploadResultPanel } from "./upload/OssUploadResultPanel";

const requiredColumns = [
  "주문번호",
  "거래처",
  "납품처",
  "주문일자",
  "품목명",
  "수량",
  "요청납기",
  "표준소요일",
  "계획여유일",
];

export const OssExcelUploadDialog = ({ open, onClose, groupCode, projectCode, onImported }) => {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fileState = useMemo(() => {
    if (!file) return { severity: "info", message: "업로드할 Excel 파일을 선택해주세요." };
    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      return { severity: "error", message: "xlsx, xls, csv 파일만 사용할 수 있습니다." };
    }
    return {
      severity: "success",
      message: `${file.name} 파일을 선택했습니다. 업로드하면 행별 검증 후 정상 행만 등록합니다.`,
    };
  }, [file]);

  const submit = async () => {
    if (!file || fileState.severity === "error") return;
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const nextResult = await importOssOrdersExcel(groupCode, projectCode, file, {
        userKey: "order.manager",
      });
      setResult(nextResult);
      onImported?.(nextResult);
    } catch (e) {
      setError(e.response?.data?.message || "Excel 업로드 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    if (submitting) return;
    setFile(null);
    setResult(null);
    setError("");
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          minHeight: 62,
          px: 2.5,
          py: 1.6,
          borderBottom: "1px solid #e5e7eb",
          fontSize: "20px !important",
          fontWeight: "800 !important",
        }}
      >
        Excel 주문 업로드 준비
      </DialogTitle>
      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Alert severity={fileState.severity}>{fileState.message}</Alert>

          <Box
            sx={{
              p: 2,
              border: "1px dashed #c7d2fe",
              borderRadius: "8px",
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "#eef2ff",
                  color: "#6c5ce7",
                }}
              >
                <TableChartOutlinedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
                  주문 헤더 1건 + 주문 품목 여러 건 구조
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: 12.5, color: "#64748b" }}>
                  주문번호가 같은 행은 하나의 주문으로 묶고, 품목 행은 개별 일정으로 계산합니다.
                </Typography>
              </Box>
            </Stack>
            <Button component="label" variant="contained" startIcon={<FileUploadOutlinedIcon />}>
              파일 선택
              <input
                hidden
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(event) => {
                  setResult(null);
                  setError("");
                  setFile(event.target.files?.[0] || null);
                }}
              />
            </Button>
          </Box>

          <Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "8px", bgcolor: "#fff" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
              기본 검증 항목
            </Typography>
            <Box
              sx={{
                mt: 1.2,
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
                gap: 0.75,
              }}
            >
              {requiredColumns.map((column) => (
                <Box
                  key={column}
                  sx={{
                    px: 1,
                    py: 0.8,
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    bgcolor: "#fbfbfe",
                    fontSize: 13,
                    color: "#374151",
                  }}
                >
                  {column}
                </Box>
              ))}
            </Box>
          </Box>

          <OssUploadResultPanel result={result} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.6, borderTop: "1px solid #e5e7eb" }}>
        <Button onClick={close} color="inherit" disabled={submitting}>닫기</Button>
        <Button
          onClick={submit}
          variant="contained"
          disabled={!file || fileState.severity === "error" || submitting}
          startIcon={<FileUploadOutlinedIcon />}
        >
          업로드 실행
        </Button>
      </DialogActions>
    </Dialog>
  );
};
